import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ReCAPTCHA from 'react-google-recaptcha'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import {
  Button,
  Label,
  Input,
  FieldGroup,
  Text,
  Link,
  PasswordInput,
  Header,
  Checkbox,
  Loader,
  Base,
  Fields,
} from '@pet-notify/ui'

import tagImage from './images/tag.png'
import ErrorMessage from 'components/ErrorMessage'
import { isEmailAvailable, isPhoneAvailable } from 'services/users'
import { useFetchOrderByActivationCode } from 'hooks/orders'
import { usePlans } from 'hooks/plans'
import type { ActivateInput, ActivateInputUser } from 'services/orders'
import OrderErrorMessage from './OrderErrorMessage'
import style from './style.module.css'
import { formatMoney } from '@pet-notify/formatters'
import LookupCodeModal from './LookupCodeModal'
import Toggle from 'sharedComponents/Toggle'
import { validatePhoneNumber } from 'services/validate-phone-number'
// import PremiumFeaturesModal from 'components/PremiumFeaturesModal'

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY

const schema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup
    .string()
    .email('Email must be a valid email address')
    .required('Email is required')
    .test('is-unique', 'Email is already in use', async (email) => {
      if (email) {
        try {
          const response = await isEmailAvailable(email)
          return response
        } catch (error) {
          return false
        }
      }
      return false
    }),
  confirmEmail: yup
    .string()
    .required('Confirm Email is required')
    .oneOf([yup.ref('email'), null], 'Must match email'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must contain 6 characters'),
  confirmPassword: yup
    .string()
    .required('Confirm Password is required')
    .oneOf([yup.ref('password'), null], 'Must match password'),
  phone: yup.string().required('Phone is required').test('is-unique', 'An account with this phone number already exists. Please log in using the Pet Notify mobile app or visit https://app.petnotify.com/signin. You can also reset your password using your registered email if needed.', async (phone) => {
    if (phone) {
      try {
        const response = await isPhoneAvailable(phone)
        return response
      } catch (error) {
        return false
      }
    }
    return true
  }),
  agree: yup

    .boolean()

    .oneOf(
      [true],
      'Please verify you understand that Pet Notify is only available in the US and Canada',
    ),
})

type FormData = ActivateInputUser & { activationCode: string } & {
  agree: boolean
  confirmEmail: string
}

type Props = {
  state: Partial<ActivateInput>
  onSubmit: (data: ActivateInputUser & { activationCode: string }) => void
}

function AccountForm({ state, ...props }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: state,
  })

  const [showLookupCodeModal, setShowLookupCodeModal] = useState(false)
  const [captchaRequired, setCaptchaRequired] = useState(false)
  const plansQuery = usePlans()
  const fetchOrderMutation = useFetchOrderByActivationCode()
  const [isPremium, setIsPremium] = useState(true)
  const [isFree, setIsFree] = useState(false)
  const [isConfirmingChange, setIsConfirmingChange] = useState(false)
  const navigate = useNavigate()
  // const [isShowingModal, setIsShowingModal] = useState(false)
  const [phoneError, setPhoneError] = useState(null)

  // console.log(`PLANQUERY :: `,plansQuery.data)
  // console.log(`STATE.PLANINTERVAL :: `,state.planInterval)

  const plan = useMemo(() => {
    if (plansQuery.data && state.planInterval) {
      return plansQuery.data.find((p) => p.interval === 'year')
    }
  }, [plansQuery, state.planInterval])

  useEffect(() => {
    reset(state)
  }, [state])

  function handleChangePlan() {
    if (isPremium) {
      // Navigate to the free-account route and set isPremium to true
      setIsPremium(false);
      navigate('free-account');
      setIsPremium(true);
    } else {
      setIsPremium(true); // Allow the user to manually toggle to Free if needed
    }
  }

  const handleLinkClick = () => {
    navigate('premium-plan')
  }


  const onSubmit = handleSubmit((data: FormData) => {
    const {
      activationCode,
      firstName,
      lastName,
      email,
      password,
      confirmPassword,
      phone,
    } = data

    const validation: any = validatePhoneNumber(data.phone)
    console.log('validation', validation)

    if (!validation.valid) {
      setPhoneError(validation.message)
      return;
    } else {
      setPhoneError(null)
    }

    fetchOrderMutation.mutate(activationCode, {
      onSuccess: () => {
        props.onSubmit({
          activationCode,
          firstName,
          lastName,
          email,
          password,
          confirmPassword,
          phone,
        })
      },
    })
  })

  // console.log('plan', plan)
  if (plansQuery.isLoading || !plan) {
    return <Loader />
  }

  return (
    <>
      {showLookupCodeModal && (
        <LookupCodeModal onClose={() => setShowLookupCodeModal(false)} />
      )}
      {/* {isShowingModal && (
        <PremiumFeaturesModal onClose={() => setIsShowingModal(false)} />
      )} */}
      <form onSubmit={onSubmit}>
        <div className={style.image}>
          <img
            src={tagImage}
            alt='Sample Pet Notify Intellitag with packaging'
          />
        </div>
        <Header as='h1' size='xlarge' mb='6'>
          Intellitag Activation
        </Header>
        <Text mb='6'>
          Welcome to Pet Notify! Enter the information below to activate your
          Pet Notify Intellitag monitoring and lost pet messaging.
        </Text>
        <Text mb='6'>
          One household account powers unlimited pet Intellitag&apos;s for only{' '}
          {/* {formatMoney(Math.floor(plan.amount / 12) / 100)} per month, paid */}
          {formatMoney(Math.floor(1995 / 12) / 100)} per month, paid
          annually. There are no commitments, and you can cancel directly
          through the Pet Notify app at anytime.
        </Text>
        <Text mb='6'>
          Upon activating your Intellitag, you can access your Pet Notify
          account using your desktop device by going to{' '}
          <Link href='https://app.petnotify.com/signin' target='_blank'>
            app.petnotify.com/signin
          </Link>{' '}
          or through Pet Notify&apos;s mobile app, which can be downloaded at
          the{' '}
          <Link
            href={import.meta.env.VITE_IOS_APP_STORE_LINK}
            target='_blank'
            rel='noopener noreferrer'
          >
            App Store
          </Link>{' '}
          or{' '}
          <Link
            href={import.meta.env.VITE_GOOGLE_PLAY_STORE_LINK}
            target='_blank'
            rel='noopener noreferrer'
          >
            Google Play
          </Link>
          .
        </Text>

        <Header as='h2' size='large' mb='6'>
          Choose your plan
        </Header>
        <Toggle
          enabledLabel='Premium'
          disabledLabel='Free'
          value={isPremium}
          name='isPremium'
          onChange={handleChangePlan}
          className={style.toggle}
        />

        <Link className={style.link} onClick={handleLinkClick}>
          What else does Premium include?
        </Link>

        <div className={style.spacer} />

        <Fields>
          <FieldGroup>
            <Label htmlFor='firstName'>First Name</Label>
            <Input
              type='text'
              id='firstName'
              {...register('firstName', { required: true })}
            />
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor='lastName'>Last Name</Label>
            <Input
              type='text'
              id='lastName'
              {...register('lastName', { required: true })}
            />
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor='phone' mb='0'>
              Phone
            </Label>

            <Text variant='note' mb='2'>
              Phone number used when you placed your order
            </Text>
            <Input id='phone' {...register('phone', { required: true })} />
          </FieldGroup>
          {errors.phone && <ErrorMessage error={errors.phone.message} />}

          <FieldGroup>
            <Label htmlFor='email'>Email</Label>
            <Input type='email' id='email' {...register('email')} />
          </FieldGroup>
          {errors.email && <ErrorMessage error={errors.email.message} />}
          <FieldGroup>
            <Label htmlFor='confirmEmail'>Confirm Email</Label>
            <Input
              type='email'
              id='confirmEmail'
              {...register('confirmEmail', { required: true })}
            />
          </FieldGroup>
          {errors.confirmEmail && (
            <ErrorMessage error={errors.confirmEmail.message} />
          )}

          <FieldGroup>
            <div className='horizontal-pair'>
              <Label htmlFor='activationCode'>Activation Code</Label>
              <Link onClick={() => setShowLookupCodeModal(true)}>
                Lost your activation code?
              </Link>
            </div>
            <Input
              type='text'
              id='activationCode'
              placeholder='6-digit activation code found in your welcome letter'
              required
              {...register('activationCode', { required: true })}
            />
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor='password' mb='0'>
              Create Password
            </Label>

            <Text variant='note' mb='2'>
              Must contain a minimum of 6 characters including a number
            </Text>
            <PasswordInput
              id='password'
              {...register('password', { required: true })}
            />
          </FieldGroup>
          {errors.password && <ErrorMessage error={errors.password.message} />}
          <FieldGroup>
            <Label htmlFor='confirmPassword'>Confirm Password</Label>
            <PasswordInput
              id='confirmPassword'
              {...register('confirmPassword', { required: true })}
            />
          </FieldGroup>

          {errors.confirmPassword && (
            <ErrorMessage error={errors.confirmPassword.message} />
          )}

         

          {fetchOrderMutation.isError && (
            <OrderErrorMessage error={fetchOrderMutation.error} />
          )}

          {/* <FieldGroup>
            <Checkbox
              label='I understand Pet Notify is only available in the US and Canada'
              id='signup-agree'
              {...register('agree')}
            />
          </FieldGroup>

          {errors.agree && <ErrorMessage error={errors.agree.message} />} */}

          {RECAPTCHA_SITE_KEY && (
            <Base mb='6'>
              <ReCAPTCHA
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={() => {
                  setCaptchaRequired(false)
                }}
              />
            </Base>
          )}
        </Fields>

        {phoneError && <ErrorMessage error={phoneError} />}

        <Button mb='8' type='submit' disabled={false}>
          Continue
        </Button>

        <Text>
          Having trouble?{' '}
          <Link href='mailto:support@petnotify.com'>Email us</Link> and
          we&apos;ll help you sort it out.
        </Text>
      </form>
    </>
  )
}

export default AccountForm
